import { yupResolver } from "@hookform/resolvers/yup"
import { useState } from "react"
import * as yup from "yup"

import DialogProvider from "@components/dialog_provider"
import TextInput from "@components/text_input"
import { useCustomerPageContext } from "@contexts/customer"
import { specialOfferPackage } from "@domain/customer/infos"
import useControlledForm from "@hooks/use_controlled_form"
import { Stack } from "@mui/material"
import { useQueryClient } from "@tanstack/react-query"
import { showErrorToast } from "@utils/toast"

// api-v2 `OPERATOR_FREE_CREDITS` for a Special plan with none set.
const DEFAULT_SPECIAL_OPERATOR_CREDITS = 500

export type OfferFormData = {
  person: number
  whatsappBusiness: number
  userSeat: number
  monthlyPrice: number
  yearlyPrice: number
  operatorCredits: number
}

export default function SpecialOfferDialog({
  isOpen,
  setClose
}: {
  isOpen: boolean
  setClose: () => void
}) {
  const { translate, currentCustomer: customer } = useCustomerPageContext()
  const queryClient = useQueryClient()

  const schema = yup
    .object({
      person: yup.number().required(),
      whatsappBusiness: yup.number().required(),
      userSeat: yup.number().required(translate("user_seat_field_is_required")),
      monthlyPrice: yup
        .number()
        .required(translate("monthly_price_field_is_required")),
      yearlyPrice: yup
        .number()
        .required(translate("yearly_price_field_is_required")),
      operatorCredits: yup.number().min(1).required()
    })
    .required()

  const {
    control,
    handleSubmit,
    reset,
    formState: { isValid }
  } = useControlledForm<OfferFormData>({
    resolver: yupResolver(schema),
    mode: "all",
    defaultValues: { operatorCredits: DEFAULT_SPECIAL_OPERATOR_CREDITS }
  })

  const [loading, setLoading] = useState(false)

  function handleClose() {
    if (loading) {
      return
    }
    setClose()
    reset()
  }

  async function onSubmit(data: OfferFormData) {
    if (!customer?.package?.customer) {
      return
    }
    setLoading(true)
    const { errorMessage } = await specialOfferPackage({
      cPerson: data.userSeat,
      customerId: customer._id,
      monthlyPrice: data.monthlyPrice,
      person: data.person,
      whatsAppBusiness: data.whatsappBusiness,
      yearlyPrice: data.yearlyPrice,
      operatorCredits: data.operatorCredits
    })
    if (!!errorMessage) {
      showErrorToast(String(errorMessage))
    }
    setLoading(false)
    handleClose()
    queryClient.invalidateQueries({ queryKey: ["special-offer"] })
  }
  return (
    <DialogProvider
      title={translate("special_offer")}
      leftButton={{
        text: translate("cancel"),
        color: "secondary",
        onClick: setClose,
        disabled: loading
      }}
      rightButton={{
        text: translate("save"),
        color: "primary",
        onClick: handleSubmit(onSubmit),
        loading: loading,
        disabled: !isValid
      }}
      isOpen={isOpen}
      setClose={handleClose}
      size="small"
    >
      <Stack gap={1}>
        <TextInput<OfferFormData>
          type="number"
          label={translate("customer_person")}
          disabled={loading}
          name="person"
          placeholder={translate("enter")}
          control={control}
        />
        <TextInput<OfferFormData>
          type="number"
          label={"Whatsapp bussiness"}
          disabled={loading}
          name="whatsappBusiness"
          placeholder={translate("enter")}
          control={control}
        />
        <TextInput<OfferFormData>
          type="number"
          label={translate("user_seat")}
          disabled={loading}
          name="userSeat"
          placeholder={translate("enter")}
          control={control}
        />
        <TextInput<OfferFormData>
          type="number"
          label={translate("operator_monthly_credits")}
          disabled={loading}
          name="operatorCredits"
          placeholder={translate("enter")}
          control={control}
        />
        <TextInput<OfferFormData>
          type="number"
          TextLeft="$"
          label={translate("monthly_price")}
          disabled={loading}
          name="monthlyPrice"
          placeholder={translate("enter")}
          control={control}
        />
        <TextInput<OfferFormData>
          type="number"
          TextLeft="$"
          label={translate("yearly_price")}
          disabled={loading}
          name="yearlyPrice"
          placeholder={translate("enter")}
          control={control}
        />
      </Stack>
    </DialogProvider>
  )
}
